<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useDatatablesStore } from '../store/index.js';
import { debounce } from '@chatwoot/utils';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BulkSelectBar from 'dashboard/components-next/captain/assistant/BulkSelectBar.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import PageEmptyState from 'escaleshop/shared/components/PageEmptyState.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const store = useDatatablesStore();

const slug = computed(() => route.params.slug);

const FIELD_TYPES = computed(() => [
  { value: 'text', label: t('DATATABLES.FIELD_TYPES.TEXT') },
  { value: 'number', label: t('DATATABLES.FIELD_TYPES.NUMBER') },
  { value: 'boolean', label: t('DATATABLES.FIELD_TYPES.BOOLEAN') },
  { value: 'date', label: t('DATATABLES.FIELD_TYPES.DATE') },
  { value: 'datetime', label: t('DATATABLES.FIELD_TYPES.DATETIME') },
  { value: 'select', label: t('DATATABLES.FIELD_TYPES.SELECT') },
  { value: 'email', label: t('DATATABLES.FIELD_TYPES.EMAIL') },
  { value: 'url', label: t('DATATABLES.FIELD_TYPES.URL') },
]);

// ─── Schema dialog ────────────────────────────────────────────────────────────
const schemaDialogRef = ref(null);
const isSavingSchema = ref(false);
const editFields = ref([]);

async function openSchemaDialog() {
  editFields.value = (store.currentTable?.schema ?? []).map(f => ({ ...f }));
  await nextTick();
  schemaDialogRef.value?.open();
}

function addSchemaField() {
  editFields.value.push({ name: '', type: 'text', required: false });
}

function removeSchemaField(idx) {
  editFields.value.splice(idx, 1);
}

async function saveSchema() {
  isSavingSchema.value = true;
  try {
    await store.updateTable(slug.value, {
      name: store.currentTable.name,
      description: store.currentTable.description,
      schema: editFields.value,
    });
    schemaDialogRef.value?.close();
    useAlert(t('DATATABLES.SHOW.SCHEMA_DIALOG.SUCCESS'));
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.join(', ') ||
        t('DATATABLES.SHOW.SCHEMA_DIALOG.ERROR')
    );
  } finally {
    isSavingSchema.value = false;
  }
}

// ─── Row dialog ───────────────────────────────────────────────────────────────
const rowDialogRef = ref(null);
const isSavingRow = ref(false);
const editingRow = ref(null);
const rowForm = ref({});

function buildRowForm(row) {
  const base = {};
  (store.currentTable?.schema ?? []).forEach(f => {
    base[f.name] = row?.data?.[f.name] ?? '';
  });
  return base;
}

async function openAddRow() {
  editingRow.value = null;
  rowForm.value = buildRowForm(null);
  await nextTick();
  rowDialogRef.value?.open();
}

async function openEditRow(row) {
  editingRow.value = row;
  rowForm.value = buildRowForm(row);
  await nextTick();
  rowDialogRef.value?.open();
}

function onRowDialogClose() {
  editingRow.value = null;
}

async function saveRow() {
  isSavingRow.value = true;
  try {
    if (editingRow.value) {
      await store.updateRow(slug.value, editingRow.value.id, rowForm.value);
    } else {
      await store.createRow(slug.value, rowForm.value);
    }
    rowDialogRef.value?.close();
    useAlert(
      editingRow.value
        ? t('DATATABLES.SHOW.ROW_DIALOG.SUCCESS_UPDATE')
        : t('DATATABLES.SHOW.ROW_DIALOG.SUCCESS_CREATE')
    );
  } catch (e) {
    useAlert(
      e?.response?.data?.errors?.join(', ') ||
        t('DATATABLES.SHOW.ROW_DIALOG.ERROR')
    );
  } finally {
    isSavingRow.value = false;
  }
}

function inputType(field) {
  const map = { number: 'number', date: 'date', datetime: 'datetime-local', email: 'email', url: 'url' };
  return map[field.type] || 'text';
}

// ─── Delete row dialog ────────────────────────────────────────────────────────
const deleteRowDialogRef = ref(null);
const deletingRow = ref(null);
const isDeletingRow = ref(false);

async function openDeleteRow(row) {
  deletingRow.value = row;
  await nextTick();
  deleteRowDialogRef.value?.open();
}

function onDeleteRowClose() {
  deletingRow.value = null;
}

async function confirmDeleteRow() {
  if (!deletingRow.value) return;
  isDeletingRow.value = true;
  try {
    await store.deleteRow(slug.value, deletingRow.value.id);
    deleteRowDialogRef.value?.close();
    useAlert(t('DATATABLES.SHOW.DELETE_ROW.SUCCESS'));
  } catch {
    useAlert(t('DATATABLES.SHOW.DELETE_ROW.ERROR'));
  } finally {
    isDeletingRow.value = false;
  }
}

// ─── Table data ───────────────────────────────────────────────────────────────
const schema = computed(() => store.currentTable?.schema ?? []);

const rowHeaders = computed(() => ['', '#', ...schema.value.map(f => f.name), '']);

// ─── Bulk selection ───────────────────────────────────────────────────────────
const selectedIds = ref(new Set());
const isDeletingSelected = ref(false);
const deleteBulkDialogRef = ref(null);

const bulkDeleteTitle = computed(() =>
  selectedIds.value.size > 1
    ? t('DATATABLES.SHOW.BULK.DELETE_DIALOG.TITLE')
    : t('DATATABLES.SHOW.BULK.DELETE_DIALOG.SINGULAR_TITLE')
);
const bulkDeleteDescription = computed(() =>
  selectedIds.value.size > 1
    ? t('DATATABLES.SHOW.BULK.DELETE_DIALOG.DESCRIPTION', { count: selectedIds.value.size })
    : t('DATATABLES.SHOW.BULK.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);
const bulkDeleteConfirmLabel = computed(() =>
  selectedIds.value.size > 1
    ? t('DATATABLES.SHOW.BULK.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('DATATABLES.SHOW.BULK.DELETE_DIALOG.CONFIRM_SINGLE')
);

function openDeleteBulkDialog() {
  if (!selectedIds.value.size || isDeletingSelected.value) return;
  deleteBulkDialogRef.value?.open();
}

async function confirmDeleteSelected() {
  isDeletingSelected.value = true;
  try {
    await Promise.all([...selectedIds.value].map(id => store.deleteRow(slug.value, id)));
    selectedIds.value = new Set();
    deleteBulkDialogRef.value?.close();
    useAlert(t('DATATABLES.SHOW.BULK.DELETE_SUCCESS'));
  } catch {
    useAlert(t('DATATABLES.SHOW.BULK.DELETE_ERROR'));
  } finally {
    isDeletingSelected.value = false;
  }
}

const allSelected = computed(
  () => store.rows.length > 0 && store.rows.every(r => selectedIds.value.has(r.id))
);
const isIndeterminate = computed(
  () => !allSelected.value && store.rows.some(r => selectedIds.value.has(r.id))
);

function toggleAll(checked) {
  const next = new Set(selectedIds.value);
  store.rows.forEach(r => (checked ? next.add(r.id) : next.delete(r.id)));
  selectedIds.value = next;
}


function cellValue(row, field) {
  const v = row.data?.[field.name];
  if (v === null || v === undefined) return '—';
  if (field.type === 'boolean') {
    const bool = v === true || v === 'true' || v === 1;
    return bool ? t('DATATABLES.SHOW.BOOLEAN_YES') : t('DATATABLES.SHOW.BOOLEAN_NO');
  }
  return v;
}

const currentPage = ref(1);
const search = ref('');

const fetchWithParams = (page = 1) =>
  store.fetchRows(slug.value, {
    page,
    ...(search.value ? { search: search.value } : {}),
  });

const onSearch = debounce(async query => {
  search.value = query;
  currentPage.value = 1;
  await fetchWithParams(1);
}, 400);

async function changePage(page) {
  currentPage.value = page;
  await fetchWithParams(page);
}

onMounted(async () => {
  await store.fetchTable(slug.value);
  await fetchWithParams();
});
</script>

<template>
  <div class="flex w-full h-full flex-col overflow-hidden bg-n-surface-1">

    <header class="sticky top-0 z-10 px-6">
      <div class="flex items-center justify-between w-full py-6 gap-2 mx-auto max-w-5xl">
        <div class="flex items-center gap-3 min-w-0">
          <button
            class="flex items-center justify-center size-8 shrink-0 rounded-lg text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
            @click="router.push(accountScopedRoute('datatables_index'))"
          >
            <span class="i-lucide-arrow-left size-4" />
          </button>
          <span class="text-xl font-medium truncate text-n-slate-12">
            {{ store.currentTable?.name ?? '...' }}
          </span>
        </div>
        <div class="flex items-center flex-shrink-0 gap-2">
          <Input
            type="search"
            :placeholder="t('DATATABLES.SHOW.SEARCH_PLACEHOLDER')"
            :custom-input-class="['h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8']"
            @input="onSearch($event.target.value)"
          >
            <template #prefix>
              <span class="i-lucide-search absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2" />
            </template>
          </Input>
          <Button
            :label="t('DATATABLES.SHOW.EDIT_SCHEMA')"
            color="slate"
            size="sm"
            @click="openSchemaDialog"
          />
          <Button :label="t('DATATABLES.SHOW.NEW_ROW')" size="sm" @click="openAddRow" />
        </div>
      </div>
    </header>

    <main class="flex-1 overflow-y-auto px-6">
      <div class="w-full mx-auto max-w-5xl">

        <template v-if="store.loading">
          <div class="flex flex-col gap-2 animate-pulse">
            <div v-for="i in 4" :key="i" class="h-12 w-full bg-n-slate-3 rounded-lg" />
          </div>
        </template>

        <PageEmptyState
          v-else-if="!schema.length"
          icon="i-lucide-columns"
          :title="t('DATATABLES.SHOW.EMPTY_SCHEMA_TITLE')"
          :description="t('DATATABLES.SHOW.EMPTY_SCHEMA_DESCRIPTION')"
        >
          <Button size="sm" icon="i-lucide-settings" @click="openSchemaDialog">
            {{ t('DATATABLES.SHOW.EDIT_SCHEMA') }}
          </Button>
        </PageEmptyState>

        <PageEmptyState
          v-else-if="!store.rows.length"
          icon="i-lucide-rows-3"
          :title="t('DATATABLES.SHOW.EMPTY_ROWS_TITLE')"
          :description="t('DATATABLES.SHOW.EMPTY_ROWS_DESCRIPTION')"
        >
          <Button size="sm" icon="i-lucide-plus" @click="openAddRow">
            {{ t('DATATABLES.SHOW.NEW_ROW') }}
          </Button>
        </PageEmptyState>

        <template v-else>
          <div class="py-3">
            <BulkSelectBar
              v-model="selectedIds"
              :all-items="store.rows"
              :select-all-label="t('DATATABLES.SHOW.BULK.SELECT_ALL', { count: store.rows.length })"
              :selected-count-label="t('DATATABLES.SHOW.BULK.SELECTED_COUNT', { count: selectedIds.size })"
              :delete-label="t('DATATABLES.SHOW.BULK.DELETE')"
              :is-loading="isDeletingSelected"
              @bulk-delete="openDeleteBulkDialog"
            >
              <template #secondary-actions>
                <Button
                  ghost
                  slate
                  sm
                  class="!px-1.5"
                  :label="t('DATATABLES.SHOW.BULK.CLEAR')"
                  @click="selectedIds = new Set()"
                />
              </template>
            </BulkSelectBar>
          </div>
          <BaseTable :headers="rowHeaders" :items="store.rows">
            <template #header-0>
              <Checkbox
                :model-value="allSelected"
                :indeterminate="isIndeterminate"
                @change="e => toggleAll(e.target.checked)"
              />
            </template>
            <template #header-1>
              <span class="text-n-slate-9">{{ t('DATATABLES.SHOW.ID_COLUMN') }}</span>
            </template>
            <template #row="{ items }">
              <BaseTableRow v-for="row in items" :key="row.id" :item="row">
                <template #default>
                  <BaseTableCell>
                    <Checkbox
                      :model-value="selectedIds.has(row.id)"
                      @change="e => {
                        const next = new Set(selectedIds);
                        e.target.checked ? next.add(row.id) : next.delete(row.id);
                        selectedIds = next;
                      }"
                    />
                  </BaseTableCell>
                  <BaseTableCell>
                    <span class="text-body-main text-n-slate-9 tabular-nums">{{ row.id }}</span>
                  </BaseTableCell>
                  <BaseTableCell v-for="field in schema" :key="field.name">
                    <span class="text-body-main text-n-slate-11 truncate max-w-48 block">
                      {{ cellValue(row, field) }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell align="end">
                    <div class="flex gap-3 justify-end flex-shrink-0">
                      <Button
                        v-tooltip.top="t('DATATABLES.SHOW.EDIT_TOOLTIP')"
                        icon="i-woot-edit-pen"
                        slate
                        sm
                        @click="openEditRow(row)"
                      />
                      <Button
                        v-tooltip.top="t('DATATABLES.SHOW.DELETE_TOOLTIP')"
                        icon="i-woot-bin"
                        slate
                        sm
                        class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                        @click="openDeleteRow(row)"
                      />
                    </div>
                  </BaseTableCell>
                </template>
              </BaseTableRow>
            </template>
          </BaseTable>

        </template>

      </div>
    </main>

    <PaginationFooter
      v-if="store.rows.length && store.rowsMeta.total_pages > 1"
      :current-page="currentPage"
      :total-items="store.rowsMeta.total"
      :items-per-page="15"
      @update:current-page="changePage"
    />

  </div>

  <!-- Schema editor dialog -->
  <Dialog
    ref="schemaDialogRef"
    :title="t('DATATABLES.SHOW.SCHEMA_DIALOG.TITLE')"
    width="xl"
    :overflow-y-auto="true"
  >
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between mb-1">
        <span class="text-sm text-n-slate-10">{{ t('DATATABLES.SHOW.SCHEMA_DIALOG.TABLE_COLUMNS') }}</span>
        <Button
          color="slate"
          size="sm"
          icon="i-lucide-plus"
          :label="t('DATATABLES.SHOW.SCHEMA_DIALOG.ADD_COLUMN')"
          @click="addSchemaField"
        />
      </div>

      <p v-if="editFields.length === 0" class="py-4 text-center text-sm text-n-slate-9">
        {{ t('DATATABLES.SHOW.SCHEMA_DIALOG.EMPTY') }}
      </p>

      <div v-else class="flex flex-col gap-2">
        <div class="grid grid-cols-[1fr_160px_auto_32px] gap-2 px-2 pb-1">
          <span class="text-xs font-medium text-n-slate-10">{{ t('DATATABLES.SHOW.SCHEMA_DIALOG.NAME') }}</span>
          <span class="text-xs font-medium text-n-slate-10">{{ t('DATATABLES.SHOW.SCHEMA_DIALOG.TYPE') }}</span>
          <span class="text-xs font-medium text-n-slate-10">{{ t('DATATABLES.SHOW.SCHEMA_DIALOG.REQUIRED') }}</span>
          <span />
        </div>
        <div
          v-for="(field, idx) in editFields"
          :key="idx"
          class="grid grid-cols-[1fr_160px_auto_32px] gap-2 items-center px-2 py-2 rounded-lg bg-n-alpha-black2"
        >
          <Input v-model="field.name" size="sm" placeholder="nome_do_campo" />
          <Select v-model="field.type" :options="FIELD_TYPES" />
          <label class="flex items-center justify-center cursor-pointer px-1">
            <input v-model="field.required" type="checkbox" class="rounded" />
          </label>
          <button
            class="flex items-center justify-center size-8 rounded-lg text-n-slate-9 hover:bg-n-ruby-3 hover:text-n-ruby-11 transition-colors"
            @click="removeSchemaField(idx)"
          >
            <span class="i-lucide-trash-2 size-4" />
          </button>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2 w-full">
        <Button variant="ghost" @click="schemaDialogRef?.close()">
          {{ t('DATATABLES.SHOW.SCHEMA_DIALOG.CANCEL') }}
        </Button>
        <Button :is-loading="isSavingSchema" :disabled="isSavingSchema" @click="saveSchema">
          {{ t('DATATABLES.SHOW.SCHEMA_DIALOG.SAVE') }}
        </Button>
      </div>
    </template>
  </Dialog>

  <!-- Row add/edit dialog -->
  <Dialog
    ref="rowDialogRef"
    :title="
      editingRow
        ? t('DATATABLES.SHOW.ROW_DIALOG.TITLE_EDIT')
        : t('DATATABLES.SHOW.ROW_DIALOG.TITLE_NEW')
    "
    width="lg"
    @close="onRowDialogClose"
  >
    <div class="flex flex-col gap-4">
      <p v-if="!schema.length" class="py-4 text-center text-sm text-n-slate-9">
        {{ t('DATATABLES.SHOW.ROW_DIALOG.NO_COLUMNS') }}
      </p>
      <template v-for="field in schema" :key="field.name">
        <label v-if="field.type === 'boolean'" class="flex items-center gap-2 cursor-pointer">
          <input v-model="rowForm[field.name]" type="checkbox" class="rounded" />
          <span class="text-sm text-n-slate-12">
            {{ field.name
            }}<span v-if="field.required" class="text-n-ruby-11 ml-0.5">{{
              t('DATATABLES.SHOW.REQUIRED_MARK')
            }}</span>
          </span>
        </label>
        <Input
          v-else
          v-model="rowForm[field.name]"
          :type="inputType(field)"
          :label="`${field.name}${field.required ? ' *' : ''}`"
          :placeholder="field.name"
        />
      </template>
    </div>
    <template #footer>
      <div class="flex justify-end gap-2 w-full">
        <Button variant="ghost" @click="rowDialogRef?.close()">
          {{ t('DATATABLES.SHOW.ROW_DIALOG.CANCEL') }}
        </Button>
        <Button :is-loading="isSavingRow" :disabled="isSavingRow" @click="saveRow">
          {{
            editingRow
              ? t('DATATABLES.SHOW.ROW_DIALOG.SAVE')
              : t('DATATABLES.SHOW.ROW_DIALOG.CREATE')
          }}
        </Button>
      </div>
    </template>
  </Dialog>

  <!-- Bulk delete confirmation -->
  <Dialog
    v-if="selectedIds.size"
    ref="deleteBulkDialogRef"
    type="alert"
    :title="bulkDeleteTitle"
    :description="bulkDeleteDescription"
    :confirm-button-label="bulkDeleteConfirmLabel"
    :is-loading="isDeletingSelected"
    @confirm="confirmDeleteSelected"
  />

  <!-- Delete row confirmation -->
  <Dialog
    ref="deleteRowDialogRef"
    type="alert"
    :title="t('DATATABLES.SHOW.DELETE_ROW.TITLE')"
    :description="t('DATATABLES.SHOW.DELETE_ROW.DESCRIPTION')"
    @close="onDeleteRowClose"
  >
    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          variant="faded"
          color="slate"
          :label="t('DATATABLES.SHOW.DELETE_ROW.CANCEL')"
          class="w-full"
          :disabled="isDeletingRow"
          @click="deleteRowDialogRef?.close()"
        />
        <Button
          color="ruby"
          :label="t('DATATABLES.SHOW.DELETE_ROW.CONFIRM')"
          class="w-full"
          :is-loading="isDeletingRow"
          :disabled="isDeletingRow"
          @click="confirmDeleteRow"
        />
      </div>
    </template>
  </Dialog>
</template>
